/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient } from '@/utils/apiClient';

import {
  CreateTicketPayload,
  CreateTicketResponse,
  UpdateTicketDataPayload,
  AddTicketItemPayload,
  DeleteTicketItemPayload,
  SubmitDocumentPayload,
  BasicMessageResponse,
  UploadDocumentResponse,
  CompleteTicketPayload,
  CompleteTicketResponse,
  SubmitAddedItemsPayload,
  SubmitAddedItemsResponse,
} from './types';

const readBackendError = async (err: any): Promise<any> => {
  if (err?.response?.data !== undefined) return err.response.data;

  if (err?.response !== undefined) {
    const resp = err.response;

    if (typeof resp === 'string') return resp;
    if (resp && typeof resp === 'object' && !('json' in resp) && !('text' in resp)) {
      return resp;
    }

    try {
      if (resp && typeof resp.json === 'function') {
        try {
          const j = await resp.json();
          return j;
        } catch {  }
      }
      if (resp && typeof resp.text === 'function') {
        const t = await resp.text();
        try {
          return JSON.parse(t);
        } catch {
          return t;
        }
      }
    } catch {  }
  }

  if (err?.data !== undefined) return err.data;

  if (typeof err === 'string') {
    try { return JSON.parse(err); } catch { return err; }
  }

  if (err instanceof Error) {
    if ((err as any).payload !== undefined) return (err as any).payload;
    return err.message || 'Request failed';
  }

  if (err && typeof err === 'object') return err;

  return 'Request failed';
};

export const createTicket = createAsyncThunk<
  CreateTicketResponse,
  CreateTicketPayload,
  { rejectValue: unknown }
>('ticketing/createTicket', async (payload, { rejectWithValue }) => {
  try {
    const response = await apiClient('/workflows/api/v1/ticketing/create/', {
      method: 'POST',
      body: payload,
    });
    return response as CreateTicketResponse;
  } catch (err: any) {
    return rejectWithValue(await readBackendError(err));
  }
});

export const updateTicketData = createAsyncThunk<
  BasicMessageResponse,
  UpdateTicketDataPayload,
  { rejectValue: unknown }
>('ticketing/updateTicketData', async (payload, { rejectWithValue }) => {
  try {
    const response = await apiClient(
      '/workflows/api/v1/ticketing/update/data/',
      {
        method: 'PATCH',
        body: payload,
      }
    );
    return response as BasicMessageResponse;
  } catch (err: any) {
    return rejectWithValue(await readBackendError(err));
  }
});

export const addTicketItem = createAsyncThunk<
  BasicMessageResponse,
  AddTicketItemPayload,
  { rejectValue: unknown }
>('ticketing/addTicketItem', async (payload, { rejectWithValue }) => {
  try {
    const response = await apiClient('/workflows/api/v1/ticketing/add/item/', {
      method: 'POST',
      body: payload,
    });
    return response as BasicMessageResponse;
  } catch (err: any) {
    return rejectWithValue(await readBackendError(err));
  }
});

export const deleteTicketItem = createAsyncThunk<
  BasicMessageResponse,
  DeleteTicketItemPayload,
  { rejectValue: unknown }
>('ticketing/deleteTicketItem', async (payload, { rejectWithValue }) => {
  try {
    const response = await apiClient(
      '/workflows/api/v1/ticketing/delete/item/',
      {
        method: 'DELETE',
        body: payload,
      }
    );
    return response as BasicMessageResponse;
  } catch (err: any) {
    return rejectWithValue(await readBackendError(err));
  }
});

export const uploadTicketDocument = createAsyncThunk<
  UploadDocumentResponse,
  SubmitDocumentPayload,
  { rejectValue: unknown }
>('ticketing/uploadTicketDocument', async (payload, { rejectWithValue }) => {
  try {
    const fd = new FormData();
    fd.append('field', payload.field);
    fd.append('file', payload.file);

    const response = await apiClient(
      '/workflows/api/v1/ticketing/add/document/',
      {
        method: 'POST',
        body: fd,
      }
    );
    return response as UploadDocumentResponse;
  } catch (err: any) {
    return rejectWithValue(await readBackendError(err));
  }
});

export const completeTicket = createAsyncThunk<
  CompleteTicketResponse,
  CompleteTicketPayload,
  { rejectValue: unknown }
>('ticketing/completeTicket', async (payload, { rejectWithValue }) => {
  try {
    const body = {
      ...payload,
      items: payload.items ?? [],
    };

    const response = await apiClient('/workflows/api/v1/ticketing/complete/', {
      method: 'POST',
      body,
    });
    return response as CompleteTicketResponse;
  } catch (err: any) {
    return rejectWithValue(await readBackendError(err));
  }
});

export const submitAddedItems = createAsyncThunk<
  SubmitAddedItemsResponse,
  SubmitAddedItemsPayload,
  { rejectValue: unknown }
>('ticketing/submitAddedItems', async (payload, { rejectWithValue }) => {
  try {
    const response = await apiClient(
      '/workflows/api/v1/ticketing/submit/items/',
      {
        method: 'POST',
        body: payload,
      }
    );
    return response as SubmitAddedItemsResponse;
  } catch (err: any) {
    return rejectWithValue(await readBackendError(err));
  }
});
